import { useEffect } from 'react';

/**
 * Simplified scrollbar visibility hook.
 * 
 * This hook only manages the `is-scrolling` class on scrollable elements,
 * which can be used by other features that depend on knowing when scroll is active.
 * 
 * Native browser scrollbars are now used with CSS styling (see index.css),
 * eliminating the jitter caused by JavaScript-driven overlay positioning
 * competing with virtualization transforms.
 */
export const useScrollbarVisibility = () => {
	useEffect(() => {
		// Track per-element timers and all timeouts for cleanup
		const timers = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>();
		const allTimeouts = new Set<ReturnType<typeof setTimeout>>();
		const allRafIds = new Set<number>();

		const onScrollCapture = (e: Event) => {
			const target = e.target as HTMLElement | null;
			if (!target || !(target instanceof HTMLElement)) return;

			// Only handle scrollable elements we style
			if (!target.classList.contains('overflow-y-auto')) return;

			// Use RAF to batch DOM writes and avoid forced reflows
			const rafId = requestAnimationFrame(() => {
				target.classList.add('is-scrolling');
				allRafIds.delete(rafId);
			});
			allRafIds.add(rafId);

			// Debounce removal so we remove the class when scrolling stops
			const existing = timers.get(target);
			if (existing) {
				clearTimeout(existing);
				allTimeouts.delete(existing);
			}

			const timeout = setTimeout(() => {
				const removeRafId = requestAnimationFrame(() => {
					target.classList.remove('is-scrolling');
					allRafIds.delete(removeRafId);
				});
				allRafIds.add(removeRafId);
				timers.delete(target);
				allTimeouts.delete(timeout);
			}, 150);

			timers.set(target, timeout);
			allTimeouts.add(timeout);
		};

		// Scroll events don't bubble, so capture at the document level
		document.addEventListener('scroll', onScrollCapture, { capture: true, passive: true });

		return () => {
			document.removeEventListener('scroll', onScrollCapture, { capture: true } as any);
			allTimeouts.forEach(clearTimeout);
			allTimeouts.clear();
			allRafIds.forEach((rafId) => cancelAnimationFrame(rafId));
			allRafIds.clear();
		};
	}, []);
};
